<template>
  <div>
    <v-btn
      color="secondary"
      rounded
      small
      :loading="loading"
      :disabled="disabled"
      @click="dialogImport = true">
      <v-icon small>
        mdi-application-import
      </v-icon>
      <span class="ml-1">
        {{ $t('button.Import') }}
      </span>
    </v-btn>
    <v-dialog
      v-model="dialogImport"
      max-width="500px"
      @click:outside="onCloseCsvDialog">
      <v-card>
        <v-card-title>Upload a File</v-card-title>

        <v-card-text class="pb-0">
          <p>
            คำเตือน! เมื่อทำการเลือกไฟล์ สินค้าใหม่จะรวมกับสินค้าก่อนหน้า
          </p>
          <v-file-input
            v-model="selectedFile"
            label="Select CSV file"
            accept=".csv"
            color="secondary"
            class="my-2"
            outlined
            dense
            prepend-icon=""
            append-icon="mdi-upload"
            hide-details
            @focus="clearValidate"
            @change="validateCsv" />
          <p
            v-if="csvValidateErrors.length"
            class="text-bold">
            Total {{ csvValidateErrors.length | showNumberFormat }} errors.
          </p>
          <div
            v-if="csvValidateErrors.length"
            class="csv-fail">
            <p
              v-for="(errText, index) in csvValidateErrors"
              :key="index"
              class="red--text">
              {{ errText }}
            </p>
          </div>
          <div class="d-flex justify-end mt-4 mb-2">
            <v-btn
              class="text-decoration-underline"
              text
              small
              color="primary"
              @click="downloadTemplateCsv">
              Download Template
            </v-btn>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            :disabled="importing"
            @click="onCloseCsvDialog">
            Close
          </v-btn>
          <v-btn
            color="secondary"
            :loading="importing"
            :disabled="csvValidateErrors.length > 0 || !selectedFile"
            @click="showConfirmation">
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ExportToCsv } from 'export-to-csv'
import ProductProvider from '@/resources/ProductProvider'

const ProductService = new ProductProvider()
const fileReader = new FileReader()

export default {
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    brand: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      masterHeaderCsv: [
        'Code'
      ],
      dialogImport: false,
      selectedFile: null,
      csvValidateErrors: [],
      importing: false
    }
  },
  methods: {
    isArrayEqual (arr1, arr2) {
      if (arr1.length !== arr2.length) {
        return false
      }

      const sorted1 = [...arr1].sort()
      const sorted2 = [...arr2].sort()

      return sorted1.every((val, index) => val === sorted2[index])
    },
    findDuplicates (arr) {
      return arr.filter((item, index) => arr.indexOf(item) !== index)
    },
    isNumberString (str) {
      return /^([0-9]*[.])?[0-9]+$/.test(str)
    },
    validateCsv () {
      this.csvValidateErrors = []

      if (!this.selectedFile) {
        return
      }

      const addRowText = (existingText, newText) => `${existingText ? ', ' : ''} ${newText}`

      fileReader.readAsText(this.selectedFile, 'utf8')
      fileReader.onload = async (readerEvent) => {
        try {
          const contents = readerEvent.target.result.split('\n')
          const elements = []

          contents.forEach((line) => {
            if (line.trim().length > 0) {
              const text = line
                .replaceAll('"', '')
                .replaceAll('\r', '')
                .split(',')
              if (!text.every((col) => col.trim() === '')) {
                elements.push(text)
              }
            }
          })

          if (!elements || !Array.isArray(elements) || !elements.length || elements.length === 1) {
            this.csvValidateErrors.push('The CSV contains no content. Please ensure the file has valid data.')
            return
          }

          const headerFields = elements[0].filter((h) => h)

          if (!this.isArrayEqual(this.masterHeaderCsv, headerFields)) {
            const missingHeaders = this.masterHeaderCsv.filter((h) => !headerFields.includes(h))

            if (missingHeaders.length) {
              this.csvValidateErrors.push(`Header : Required headers are missing: ${missingHeaders.join(', ')}. Please add them.`)
            }

            const duplicateField = this.findDuplicates(headerFields)

            if (duplicateField.length) {
              this.csvValidateErrors.push(`Header : ${duplicateField.join(', ')} fields are duplicated`)
            }

            return
          }

          for (const [index, element] of elements.entries()) {
            if (index !== 0) {
              const productInfo = Object.fromEntries(headerFields.map((key, mapIndex) => [key, element[mapIndex]]))

              let rowFailMsg = ''

              if (!productInfo['Code']) {
                rowFailMsg += addRowText(rowFailMsg, 'Code')
              }

              if (rowFailMsg) {
                rowFailMsg = `Row ${index + 1} : ${rowFailMsg} must be a valid \n`
                this.csvValidateErrors.push(rowFailMsg)
              }
            }
          }
        } catch (e) {
          this.csvValidateErrors.push('Something wrong on CSV validate')
        }
      }
    },
    showConfirmation () {
      this.$store.dispatch('Components/setModal', {
        value: true,
        title: 'Import Products',
        message: 'คำเตือน! เมื่อทำการยืนยัน ข้อมูลสินค้าก่อนหน้าจะรวมกับสินค้าใหม่',
        confirmText: 'Confirm',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.onConfirmImport()
      })
    },
    onConfirmImport () {
      try {
        this.importing = true

        const selectedProducts = []
        const productNotFounds = []
        const productsFromCsv = []
        const dataFromCsv = []

        fileReader.readAsText(this.selectedFile, 'utf8')
        fileReader.onload = async (readerEvent) => {
          const contents = readerEvent.target.result.split('\n')
          contents.forEach((line) => {
            if (line.trim().length > 0) {
              const text = line
                .replaceAll('"', '')
                .replaceAll('\r', '')
                .split(',')
              if (!text.every((col) => col.trim() === '')) {
                dataFromCsv.push(text)
              }
            }
          })

          const csvHeaderAsArray = dataFromCsv[0]
          for (const [index, productAsArray] of dataFromCsv.entries()) {
            if (index !== 0) {
              const productInfo = Object.fromEntries(csvHeaderAsArray.map((key, mapIndex) => [key, productAsArray[mapIndex]]))

              const codes = productInfo.Code.split('.')
              let model = productInfo.Code

              if (codes.length === 7) {
                model = `${codes[0]}.${codes[1]}.${codes[2]}.${codes[3]}.${codes[4]}.${codes[5]}`
              }

              productInfo.model = model
              productsFromCsv.push(productInfo)
            }
          }

          const models = [...new Set(productsFromCsv.map((product) => product.model))]
          const { data: products } = await ProductService.getProductsByModelsForB2B({
            models,
            brand: this.brand
          })

          for (const [index, productFromCsv] of productsFromCsv.entries()) {
            const productInfo = products.find((product) => product.model === productFromCsv.model)

            if (productInfo) {
              const duplicated = selectedProducts.findIndex((selected) => selected.model === productInfo.model)

              if (duplicated === -1) {
                selectedProducts.push(productInfo)
              }
            } else {
              productNotFounds.push({
                ...productFromCsv,
                row: index + 1
              })
            }
          }

          if (productNotFounds.length) {
            productNotFounds.forEach((v) => {
              this.csvValidateErrors.push(`Row ${v.row} : ไม่พบสินค้า ${v.Code}`)
            })
          } else {
            this.onSubmitImport(selectedProducts)
          }

          this.importing = false
        }
      } catch (error) {
        console.error('onConfirmImport', error)
        this.setSnackbar({
          value: true,
          message: 'ไฟล์ไม่สามารถใช้งานได้กรุณาตรวจสอบ',
          type: 'error',
          timeout: 6000
        })

        this.importing = false
      }
    },
    clearValidate () {
      this.selectedFile = null
      this.csvValidateErrors = []
    },
    onCloseCsvDialog () {
      this.dialogImport = false
      this.clearValidate()
    },
    onSubmitImport (products) {
      this.$emit('import', products)

      this.onCloseCsvDialog()
    },
    async downloadTemplateCsv () {
      const mapped = [
        this.masterHeaderCsv.reduce((obj, header) => ({ ...obj, [header]: '' }), {})
      ]

      const options = {
        filename: 'products-import-template',
        showLabels: true,
        useKeysAsHeaders: true
      }

      const csvExporter = new ExportToCsv(options)
      csvExporter.generateCsv(mapped)
    }
  }
}
</script>

<style scoped>

</style>
